function todayPlus(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export type ChildColor = 'orange' | 'blue';

export type Child = {
  id: string;
  firstName: string;
  color: ChildColor;
};

export const children: Child[] = [
  { id: 'jeanne', firstName: 'Jeanne', color: 'orange' },
  { id: 'remi', firstName: 'Rémi', color: 'blue' },
];

export type SignatureItem = {
  id: string;
  childId?: Child['id'];
  label: string;
  kind:
    | 'cgu'
    | 'devis'
    | 'photo'
    | 'temoignage'
    | 'exercice'
    | 'questionnaire'
    | 'rdv';
  createdAt: string;
};

export const pendingSignatures: SignatureItem[] = [
  {
    id: 'sig-cgu',
    label: "Nouvelles conditions générales d'utilisation",
    kind: 'cgu',
    createdAt: todayPlus(-12),
  },
  {
    id: 'sig-devis',
    childId: 'jeanne',
    label: 'Devis traitement',
    kind: 'devis',
    createdAt: todayPlus(-3),
  },
];

export type AppointmentRequest = {
  id: string;
  childId: Child['id'];
  reason: string;
  requestedBy: string;
  createdAt: string;
};

export const appointmentRequests: AppointmentRequest[] = [
  {
    id: 'req-jeanne-controle',
    childId: 'jeanne',
    reason: 'Contrôle trimestriel',
    requestedBy: 'Dr Renoud',
    createdAt: todayPlus(-1),
  },
  {
    id: 'req-remi-pose',
    childId: 'remi',
    reason: 'Pose attaches',
    requestedBy: 'Dr Renoud',
    createdAt: todayPlus(-5),
  },
];

export type PendingTestimonial = {
  id: string;
  childId: Child['id'];
  prompt: string;
  createdAt: string;
};

export const pendingTestimonials: PendingTestimonial[] = [
  {
    id: 'temoin-jeanne',
    childId: 'jeanne',
    prompt: 'Votre avis sur le traitement',
    createdAt: todayPlus(-25),
  },
];

export type PendingQuestionnaire = {
  id: string;
  childId: Child['id'];
  label: string;
  createdAt: string;
};

export const pendingQuestionnaires: PendingQuestionnaire[] = [
  {
    id: 'quest-remi-douleur',
    childId: 'remi',
    label: 'Évaluation de la douleur',
    createdAt: todayPlus(-2),
  },
];

export type Payment = {
  id: string;
  label: string;
  date: string;
  amountCents: number;
  paidCents: number;
};

export const payments: Payment[] = [
  {
    id: 'pay-consult',
    label: 'Consultation',
    date: '2 Févr. 2027',
    amountCents: 2600,
    paidCents: 2600,
  },
  {
    id: 'pay-gouttiere',
    label: 'Pose gouttières',
    date: '15 Mars 2027',
    amountCents: 31400,
    paidCents: 0,
  },
];

export const paymentsTotalPaidCents = 2600;
export const paymentsTotalAmountCents = 34000;

export type Appointment = {
  id: string;
  childId: Child['id'];
  isoDate: string; // YYYY-MM-DD
  time: string;
  kind: string;
};

export const upcomingAppointments: Appointment[] = [
  {
    id: 'rdv-jeanne',
    childId: 'jeanne',
    isoDate: todayPlus(0),
    time: '14h00',
    kind: 'Contrôle',
  },
  {
    id: 'rdv-remi',
    childId: 'remi',
    isoDate: todayPlus(21),
    time: '09h30',
    kind: 'Pose attaches',
  },
];

export const pastAppointments: Appointment[] = [
  {
    id: 'past-jeanne-1',
    childId: 'jeanne',
    isoDate: todayPlus(-45),
    time: '12h00',
    kind: 'Contrôle',
  },
  {
    id: 'past-remi-1',
    childId: 'remi',
    isoDate: todayPlus(-72),
    time: '10h15',
    kind: 'Pose gouttières',
  },
];

export type AlignerProgress = {
  childId: Child['id'];
  daysLeft: number;
  totalDays: number;
  currentStep: number;
  totalSteps: number;
};

export const alignerProgress: AlignerProgress[] = [
  {
    childId: 'jeanne',
    daysLeft: 3,
    totalDays: 14,
    currentStep: 7,
    totalSteps: 24,
  },
  {
    childId: 'remi',
    daysLeft: 3,
    totalDays: 14,
    currentStep: 4,
    totalSteps: 18,
  },
];

export type ExerciseItem = {
  id: string;
  childId: Child['id'];
  label: string;
  doneToday: boolean;
  streakDays: number;
};

export const exercises: ExerciseItem[] = [
  {
    id: 'ex-jeanne',
    childId: 'jeanne',
    label: 'Élastiques + brossage',
    doneToday: true,
    streakDays: 12,
  },
  {
    id: 'ex-remi',
    childId: 'remi',
    label: 'Brossage 3x/jour',
    doneToday: false,
    streakDays: 5,
  },
];

export type PhotoItem = {
  id: string;
  month: string;
  count: number;
};

export const photoMonths: PhotoItem[] = [
  { id: 'janv-2027', month: 'Janvier 2027', count: 4 },
  { id: 'dec-2026', month: 'Décembre 2026', count: 6 },
  { id: 'nov-2026', month: 'Novembre 2026', count: 3 },
];

export type ActionItem = {
  id: string;
  label: string;
};

export const actions: ActionItem[] = [
  { id: 'act-fil-detache', label: 'Bas fixe à se tordre / le fil se balade' },
  { id: 'act-douleur', label: 'Provoque le bruit des dents de mes enfants' },
];

export type TeamMember = {
  id: string;
  initials: string;
  fullName: string;
  role: string;
  photoUrl: string;
};

// Portraits générés (thispersondoesnotexist style) — randomuser.me sert des IDs stables.
const portrait = (gender: 'women' | 'men', n: number) =>
  `https://randomuser.me/api/portraits/${gender}/${n}.jpg`;

export const teamMembers: TeamMember[] = [
  {
    id: 'dr-renoud',
    initials: 'DR',
    fullName: 'Dr Renoud',
    role: 'Orthodontiste',
    photoUrl: portrait('women', 65),
  },
  {
    id: 'as-julie',
    initials: 'JL',
    fullName: 'Julie L.',
    role: 'Assistante',
    photoUrl: portrait('women', 43),
  },
  {
    id: 'as-sophie',
    initials: 'SP',
    fullName: 'Sophie P.',
    role: 'Secrétaire',
    photoUrl: portrait('women', 27),
  },
];

export type Conversation = {
  id: string;
  withName: string;
  preview: string;
  lastMessageAt: string;
  unread: number;
  photoUrl?: string;
};

export const conversations: Conversation[] = [
  {
    id: 'conv-cabinet',
    withName: 'Cabinet',
    preview: 'Bonjour, votre rendez-vous du 22 mai est bien noté.',
    lastMessageAt: 'hier',
    unread: 1,
  },
  {
    id: 'conv-dr-renoud',
    withName: 'Dr Renoud',
    preview: 'Pensez à porter les élastiques 22h/24.',
    lastMessageAt: '12 Déc.',
    unread: 0,
    photoUrl: portrait('women', 65),
  },
];

export type ContactAction = {
  id: string;
  label: string;
  description: string;
};

export const contactActions: ContactAction[] = [
  {
    id: 'envoi-photo',
    label: 'Envoyer une photo',
    description: 'Au cabinet — réponse sous 48h',
  },
  {
    id: 'demande-absence',
    label: "Prévenir d'une absence",
    description: 'Pour un rendez-vous à venir',
  },
  {
    id: 'annuler-rdv',
    label: 'Annuler un rendez-vous',
    description: 'Avec un délai de 24h minimum',
  },
];

export type SearchSection = {
  id: string;
  label: string;
};

export const searchSections: SearchSection[] = [
  { id: 'rdv', label: 'Rendez-vous' },
  { id: 'paiements', label: 'Paiements' },
  { id: 'documents', label: 'Documents' },
  { id: 'photos', label: 'Photos' },
  { id: 'signatures', label: 'Signatures' },
  { id: 'astuces', label: 'Astuces' },
  { id: 'seances-photos', label: 'Séances photos' },
];

export type AppointmentReason = {
  id: string;
  label: string;
  duration: string;
  description: string;
};

export const appointmentReasons: AppointmentReason[] = [
  {
    id: 'controle',
    label: 'Contrôle',
    duration: '15 min',
    description: 'Suivi régulier du traitement en cours',
  },
  {
    id: 'consultation',
    label: 'Consultation',
    duration: '30 min',
    description: 'Premier rendez-vous ou bilan complet',
  },
  {
    id: 'urgence',
    label: 'Urgence',
    duration: '20 min',
    description: 'Bague décollée, fil qui pique, douleur',
  },
  {
    id: 'seance-photo',
    label: 'Séance photo',
    duration: '20 min',
    description: 'Photos de suivi pour le dossier clinique',
  },
];

export type TimeSlot = {
  id: string;
  date: string;
  weekday: string;
  times: string[];
};

function makeSlot(daysFromToday: number, times: string[]): TimeSlot {
  const iso = todayPlus(daysFromToday);
  const d = new Date(`${iso}T00:00:00`);
  const weekday = new Intl.DateTimeFormat('fr-FR', { weekday: 'long' }).format(
    d,
  );
  const dateLabel = new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
  return {
    id: `d-${iso}`,
    date: dateLabel,
    weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1),
    times,
  };
}

export const availableSlots: TimeSlot[] = [
  makeSlot(3, ['09h00', '10h30', '14h15', '16h00']),
  makeSlot(6, ['09h30', '11h00']),
  makeSlot(9, ['08h30', '14h00', '17h45']),
  makeSlot(12, ['10h00', '11h30']),
];

export type EmergencyKind = {
  id: string;
  label: string;
  example: string;
};

export const emergencyKinds: EmergencyKind[] = [
  {
    id: 'bague-decollee',
    label: 'Bague décollée',
    example: "Un bracket s'est détaché",
  },
  {
    id: 'fil-pique',
    label: 'Fil qui pique',
    example: 'Un fil dépasse et blesse la joue',
  },
  {
    id: 'gouttiere-cassee',
    label: 'Gouttière cassée',
    example: "L'aligneur est fissuré",
  },
  {
    id: 'douleur',
    label: 'Douleur intense',
    example: 'Douleur persistante depuis 24h',
  },
  {
    id: 'autre',
    label: 'Autre problème',
    example: 'Décrivez en quelques mots',
  },
];

export type PhotoSession = {
  id: string;
  childId: Child['id'];
  label: string;
  dueDate: string;
  plancheId: number;
  status: 'todo' | 'in-progress' | 'done';
  completedShots: number;
};

export const photoSessions: PhotoSession[] = [
  {
    id: 'ps-jeanne-mai',
    childId: 'jeanne',
    label: 'Suivi mensuel — Mai 2027',
    dueDate: '20 Mai 2027',
    plancheId: 4,
    status: 'todo',
    completedShots: 0,
  },
  {
    id: 'ps-remi-mai',
    childId: 'remi',
    label: 'Avant pose — Pré-traitement',
    dueDate: '25 Mai 2027',
    plancheId: 3,
    status: 'todo',
    completedShots: 0,
  },
];

export type Tip = {
  id: string;
  question: string;
  category: 'urgence' | 'hygiene' | 'aligneur' | 'general';
  excerpt: string;
};

export const tips: Tip[] = [
  {
    id: 'tip-boitier',
    question: 'Que faire si un boîtier orthodontique se casse ?',
    category: 'urgence',
    excerpt:
      'Pas de panique : récupérez le boîtier, glissez de la cire orthodontique sur la zone qui pique et contactez le cabinet sous 48h.',
  },
  {
    id: 'tip-brosse',
    question: 'Comment se brosser les dents de manière efficace ?',
    category: 'hygiene',
    excerpt:
      'Trois brossages par jour de 2 minutes, en faisant des petits cercles, avec une brosse souple. Pensez au fil dentaire et au brossette.',
  },
  {
    id: 'tip-aligneur',
    question: 'Mon aligneur ne rentre plus, est-ce normal ?',
    category: 'aligneur',
    excerpt:
      "Si vous l'avez peu porté la veille, c'est normal. Reportez-le à fond pendant 24h. Si la gêne persiste, prenez contact.",
  },
  {
    id: 'tip-fil',
    question: 'Un fil dépasse et me blesse, que faire ?',
    category: 'urgence',
    excerpt:
      'Coupez le fil avec un coupe-ongles désinfecté ou rabattez-le avec un crayon, puis envoyez une photo au cabinet.',
  },
  {
    id: 'tip-douleur',
    question: 'Combien de temps dure la douleur après une pose ?',
    category: 'general',
    excerpt:
      'Entre 24h et 5 jours, plus marquée à J+2/3. Privilégiez du paracétamol, mâchez doucement, évitez les aliments durs.',
  },
];

export type ChatMessage = {
  id: string;
  from: 'me' | 'cabinet';
  text: string;
  at: string;
};

export type ChatThread = {
  id: string;
  contactName: string;
  contactRole: string;
  initials: string;
  photoUrl?: string;
  messages: ChatMessage[];
};

export const chatThread: ChatThread = {
  id: 'thread-cabinet',
  contactName: 'Cabinet du Dr Renoud',
  contactRole: 'Réponse en moyenne sous 4h (jours ouvrés)',
  initials: 'CR',
  photoUrl: portrait('women', 65),
  messages: [
    {
      id: 'm-1',
      from: 'cabinet',
      text: 'Bonjour Madame, votre rendez-vous du 22 mai est bien noté pour Jeanne.',
      at: 'Mar. 9h12',
    },
    {
      id: 'm-2',
      from: 'cabinet',
      text: "N'oubliez pas le carnet de santé et les dernières radios si vous les avez.",
      at: 'Mar. 9h13',
    },
    {
      id: 'm-3',
      from: 'me',
      text: 'Bonjour, merci ! Je ramènerai bien les radios. Petite question : est-ce que Rémi peut venir aussi ce jour-là pour un contrôle ?',
      at: 'Mar. 14h02',
    },
    {
      id: 'm-4',
      from: 'cabinet',
      text: 'Oui sans problème. Je vous propose un créneau enchaîné à 11h30. Je vous le confirme dans la journée.',
      at: 'Mar. 14h45',
    },
    {
      id: 'm-5',
      from: 'me',
      text: 'Parfait, merci beaucoup !',
      at: 'Mar. 14h47',
    },
    {
      id: 'm-6',
      from: 'cabinet',
      text: 'Créneau confirmé pour Rémi à 11h30. À mercredi prochain 😊',
      at: 'hier · 17h21',
    },
  ],
};

export type DocumentRecord = {
  id: string;
  childId: Child['id'];
  label: string;
  detail: string;
  status: 'received' | 'missing';
  category: 'admin' | 'mutuelle' | 'clinique';
};

export const documentRecords: DocumentRecord[] = [
  {
    id: 'doc-secu-2027',
    childId: 'jeanne',
    label: 'Attestation Sécurité Sociale',
    detail: '2027',
    status: 'received',
    category: 'admin',
  },
  {
    id: 'doc-mutu',
    childId: 'remi',
    label: 'Carte de mutuelle à jour',
    detail: 'À renouveler',
    status: 'missing',
    category: 'mutuelle',
  },
  {
    id: 'doc-pano',
    childId: 'jeanne',
    label: 'Radiographie panoramique',
    detail: '12 Mars 2027',
    status: 'received',
    category: 'clinique',
  },
  {
    id: 'doc-cephalo',
    childId: 'remi',
    label: 'Téléradiographie de profil',
    detail: 'À fournir au cabinet',
    status: 'missing',
    category: 'clinique',
  },
  {
    id: 'doc-devis',
    childId: 'jeanne',
    label: 'Devis traitement signé',
    detail: 'Signé le 4 Janvier 2027',
    status: 'received',
    category: 'admin',
  },
];
